import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Box, Typography } from "@material-ui/core";
import CloseRoundedIcon from "@material-ui/icons/CloseRounded";
import InfoRoundedIcon from "@material-ui/icons/InfoRounded";
import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import ErrorRoundedIcon from "@material-ui/icons/ErrorRounded";

import api from "../../services/api";

/**
 * PlatformBanner — in-CRM banner for super-admin broadcasts.
 *
 * Reads GET /api/notifications/active and renders any unacknowledged
 * banners at the top of the tenant workspace. Acknowledgement (dismiss)
 * is local-storage only — same notification can resurface if the user
 * clears storage, which is acceptable for ops announcements.
 *
 * Re-fetches every 5 minutes so a freshly-composed announcement appears
 * without a full page reload, but doesn't hammer the endpoint.
 */

const POLL_MS = 5 * 60_000;
const DISMISSED_KEY = "whapiz.platformBannerDismissed";

const SEVERITY_TONE = {
  info: { bg: "rgba(96,165,250,0.10)", fg: "#1d4ed8", border: "rgba(96,165,250,0.4)" },
  success: { bg: "rgba(46,227,122,0.12)", fg: "#0e6c61", border: "rgba(46,227,122,0.4)" },
  warning: { bg: "rgba(251,191,36,0.14)", fg: "#92400e", border: "rgba(251,191,36,0.5)" },
  critical: { bg: "rgba(252,165,165,0.20)", fg: "#b91c1c", border: "rgba(252,165,165,0.6)" },
};

const useStyles = makeStyles(() => ({
  wrap: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "8px 12px 0",
  },
  banner: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid",
    fontSize: 13,
    lineHeight: 1.4,
  },
  icon: {
    flexShrink: 0,
    marginTop: 1,
    "& svg": { fontSize: 18 },
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontWeight: 700,
    fontSize: 13,
    marginBottom: 2,
  },
  text: {
    fontSize: 12.5,
    opacity: 0.95,
    whiteSpace: "pre-line",
    wordBreak: "break-word",
  },
  closeBtn: {
    flexShrink: 0,
    padding: 4,
    color: "inherit",
    opacity: 0.7,
    "&:hover": { opacity: 1 },
  },
}));

const IconForSeverity = ({ severity }) => {
  if (severity === "success") return <CheckCircleRoundedIcon />;
  if (severity === "warning") return <WarningRoundedIcon />;
  if (severity === "critical") return <ErrorRoundedIcon />;
  return <InfoRoundedIcon />;
};

const readDismissed = () => {
  try {
    const raw = window.localStorage.getItem(DISMISSED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr.map(Number) : []);
  } catch {
    return new Set();
  }
};

const writeDismissed = (set) => {
  try {
    window.localStorage.setItem(
      DISMISSED_KEY,
      JSON.stringify(Array.from(set))
    );
  } catch {
    /* storage disabled — banner will resurface next mount, acceptable */
  }
};

const PlatformBanner = () => {
  const classes = useStyles();
  const [rows, setRows] = useState([]);
  const [dismissed, setDismissed] = useState(() =>
    typeof window !== "undefined" ? readDismissed() : new Set()
  );

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/notifications/active");
      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch {
      // Non-fatal — leave banners as they were. We don't want a 401 on
      // this background fetch to show an angry toast across the CRM.
    }
  }, []);

  useEffect(() => {
    load();
    const id = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  const onDismiss = (id) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      writeDismissed(next);
      return next;
    });
  };

  const visible = useMemo(
    () => rows.filter((r) => !dismissed.has(r.id)),
    [rows, dismissed]
  );

  if (visible.length === 0) return null;

  return (
    <Box className={classes.wrap}>
      {visible.map((row) => {
        const tone = SEVERITY_TONE[row.severity] || SEVERITY_TONE.info;
        return (
          <Box
            key={row.id}
            className={classes.banner}
            style={{
              background: tone.bg,
              borderColor: tone.border,
              color: tone.fg,
            }}
            role="status"
          >
            <span className={classes.icon}>
              <IconForSeverity severity={row.severity} />
            </span>
            <Box className={classes.body}>
              {row.title && (
                <Typography className={classes.title} component="div">
                  {row.title}
                </Typography>
              )}
              <Typography className={classes.text} component="div">
                {row.body}
              </Typography>
            </Box>
            <IconButton
              className={classes.closeBtn}
              size="small"
              onClick={() => onDismiss(row.id)}
              aria-label="Dismiss"
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      })}
    </Box>
  );
};

export default PlatformBanner;
